import { useInput, type InputProps } from "react-admin";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  GOOGLE_CALENDAR_COLOR_CHOICES,
  googleCalendarColorHex,
} from "@/utils/google-calendar-event-colors";

/**
 * Select Google Calendar event colorId ("1"–"11") for booking purposes.
 * Empty value means use the product default (Peacock / 7) on push.
 */
export function GoogleCalendarColorInput(props: InputProps) {
  const { field, fieldState, isRequired, id } = useInput(props);
  const raw = typeof field.value === "string" ? field.value : "";
  const selectedHex = googleCalendarColorHex(raw);

  return (
    <FormControl fullWidth error={!!fieldState.error} required={isRequired} margin="dense">
      <FormLabel
        required={isRequired}
        error={!!fieldState.error}
        htmlFor={id}
        sx={{
          mb: 0.5,
          display: "block",
          typography: "body2",
          color: fieldState.error ? "error.main" : "text.primary",
        }}
      >
        {props.label ?? "Google Calendar color"}
      </FormLabel>
      <Stack direction="row" alignItems="center" gap={1.5} useFlexGap flexWrap="wrap">
        <Select
          id={id}
          size="small"
          displayEmpty
          value={raw}
          onChange={(e) => field.onChange(e.target.value)}
          onBlur={field.onBlur}
          sx={{ minWidth: 220 }}
          renderValue={(value) => {
            if (!value) return "Default (Peacock)";
            const choice = GOOGLE_CALENDAR_COLOR_CHOICES.find((c) => c.id === value);
            return choice ? `${choice.label} (${choice.id})` : String(value);
          }}
        >
          <MenuItem value="">
            <em>Default (Peacock)</em>
          </MenuItem>
          {GOOGLE_CALENDAR_COLOR_CHOICES.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 0.5,
                    bgcolor: c.hex,
                    border: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  {c.label} ({c.id})
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
        {selectedHex && (
          <Box
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              borderRadius: 0.75,
              bgcolor: selectedHex,
              border: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
      {(props.helperText || fieldState.error?.message) && (
        <FormHelperText error={!!fieldState.error}>
          {fieldState.error?.message ?? props.helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
